import { createRef, FunctionComponent, useContext, useEffect } from 'react'
import { Context3D } from './utils/3d-context'
import { setupCamera } from './utils/commonLib/camera'
import {
  customLoadingUIText,
  runRenderLoop,
  setupEngine,
} from './utils/commonLib/engine'
// import { loadModel, customMeshes } from './utils/model'
import { setupScene } from './utils/commonLib/scene'
import { Box, ChakraProvider, Flex, Spinner } from '@chakra-ui/react'
import { getModelConfiguration } from './utils/Datasource'
import { setupDefaultLightRoom } from './utils/commonLib/light'

import './studio.css'

import DesignPanel from './components/designPanel'

import { preloadEnvs, setDefaultEnvironment } from './utils/environment'

import { skyBoxes } from '../_mocks_/skybox'
import { Scene } from 'babylonjs/scene'
import { setupFloor } from './utils/objectUtils'
import { loadModel } from './utils/modelEngine/loadModel'
import {
  getProductDesignIdFromURL,
  loadDesignData,
  loadDesignState,
  saveDesignState,
} from './utils/design/saveLoad'

import { theme } from './utils/theme/Theme'
import { AppContext } from './libs/context'
import { upsertVariant } from './utils/modelEngine/product'
import { VariantType } from './type/backendType'
import { colorLibrary } from '../_mocks_/colors'
import { textures } from '../_mocks_/textures'

// TODO LIST
// VIEW CONTROL - BY DEGREE and ON TOP VIEW

interface IStudio3D {
  width?: number
  height?: number
}

export const Studio3D: FunctionComponent<IStudio3D> = props => {
  // const { width = window.innerWidth - 350, height = window.innerHeight } = props

  const canvasRef = createRef<HTMLCanvasElement>()
  const fpsRef = createRef<HTMLDivElement>()

  const designId = getProductDesignIdFromURL()

  const context = useContext(AppContext)
  const { model, shop, ready, setModel, setReady } = context

  useEffect(() => {
    return () => {
      Context3D.get().engine?.dispose() // prevent hotload expensive duplicate added engine
      // window.location.reload()
    }
  }, [])

  useEffect(() => {
    if (shop) {
      if (!model && !ready) {
        getModelConfiguration(context).then(modelConfig => {
          setModel(modelConfig.model)
        })
      }

      if (designId) {
        loadDesignState(designId, context).then(design => {
          if (design) {
            saveDesignState(design)
          }
        })
      }
    }
  }, [shop])

  useEffect(() => {
    if (shop && model?.current?.id && !ready) {
      initialize().then(() => {
        loadDesignData(model.current, context, designId)

        upsertVariant(
          '3d-colors-library',
          'Color Library',
          VariantType.COLOR,
          colorLibrary,
          shop
        )
        upsertVariant(
          '3d-textures-library',
          'Texture Library',
          VariantType.MATERIAL,
          textures,
          shop
        )

        setReady()
      })
    }
  }, [model?.current?.id, shop])

  async function initialize() {
    if (!canvasRef.current || !model?.current) return
    if (fpsRef.current) Context3D.set({ fpsIndicator: fpsRef.current })

    setupEngine({}, canvasRef.current)
    setupScene()
    setupCamera({ speed: 1 })

    customLoadingUIText('Loading model')
    setDefaultEnvironment()
    await loadModel(model.current)
    // customMeshes(model.meshesConfig || [])

    setupFloor()
    setupDefaultLightRoom()

    // customLoadingUIText('Preload textures assets')
    // await preloadTexture(textureAssets) // preload all texture

    await preloadEnvs(
      skyBoxes.filter(
        r => r.type === '3d' && r.modelIds?.includes(model.current.id)
      ),
      Context3D.get().scene as Scene
    )

    console.log('*** INITIAL LOAD DONE ***')
    Context3D.get().engine?.hideLoadingUI()
    customLoadingUIText('')

    runRenderLoop()
  }

  if (!model?.current) return <Spinner />

  return (
    <ChakraProvider theme={theme}>
      <Flex color="white" id="main-container">
        <Box flex="1" id="babylon-container">
          <canvas ref={canvasRef} id="babylon-canvas" />
        </Box>
        <Box flex="2" id="panel-container">
          {designId && <DesignPanel />}
        </Box>
      </Flex>

      {/* show fps indicator */}
      <div id="fps_indicator" ref={fpsRef}></div>
    </ChakraProvider>
  )
}
